import { Link } from 'react-router-dom';
import { useCategories } from '@/hooks/useCategories';
import { AnimateOnScroll } from './AnimateOnScroll';
import { Skeleton } from '@/components/ui/skeleton';

function CategorySkeleton() {
  return (
    <div className="aspect-square rounded-2xl overflow-hidden w-full max-w-[180px]">
      <Skeleton className="w-full h-full" />
    </div>
  );
}

export function CategoryGrid() {
  const { data: categories, isLoading } = useCategories();

  const filteredCategories = categories?.filter((c) => c.slug !== 'all');

  if (isLoading) {
    return (
      <section className="py-10 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <Skeleton className="h-8 w-40 mx-auto mb-3" />
            <Skeleton className="h-5 w-60 mx-auto" />
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {[...Array(5)].map((_, i) => (
              <CategorySkeleton key={i} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-10 bg-background overflow-hidden">
      <div className="container mx-auto px-4">
        <AnimateOnScroll animation="fade-in-up" className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            הקטגוריות שלנו
          </h2>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto">
            בחרו את הקטגוריה המועדפת עליכם
          </p>
        </AnimateOnScroll>

        <div className="flex flex-wrap justify-center gap-4">
          {filteredCategories?.map((category, index) => (
            <AnimateOnScroll
              key={category.id}
              animation="scale-in"
              delay={index * 80}
            >
              <Link
                to={`/meals?category=${category.slug}`}
                className="group relative aspect-square overflow-hidden rounded-2xl shadow-lg block w-[150px] sm:w-[160px] md:w-[180px]"
              >
                {/* Background Image */}
                <div
                  className="absolute inset-0 bg-cover bg-center transition-all duration-700 group-hover:scale-110"
                  style={{
                    backgroundImage: category.image_url
                      ? `url(${category.image_url})`
                      : 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--accent)) 100%)',
                  }}
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent transition-all duration-500 group-hover:from-black/85 group-hover:via-black/40" />
                
                {/* Shine Effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
                </div>
                
                {/* Content */}
                <div className="absolute inset-0 flex items-end p-3">
                  <div className="w-full">
                    <h3 className="text-white text-base md:text-lg font-bold text-center transform transition-all duration-500 group-hover:-translate-y-2 group-hover:scale-105">
                      {category.name_he}
                    </h3>
                    
                    {/* Animated Underline */}
                    <div className="h-0.5 bg-gradient-to-r from-transparent via-accent to-transparent mx-auto mt-1.5 w-0 group-hover:w-14 transition-all duration-500 ease-out" />
                    
                    {/* Explore Text */}
                    <p className="text-white/0 group-hover:text-white/80 text-xs text-center mt-1 transition-all duration-500 transform translate-y-3 group-hover:translate-y-0">
                      לחצו לצפייה
                    </p>
                  </div>
                </div>
                
                {/* Border Glow */}
                <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-accent/50 transition-all duration-500" />
              </Link>
            </AnimateOnScroll>
          ))}
        </div>
      </div>
    </section>
  );
}
